import React from 'react';
import { ArrowRight, Bot, Workflow, Database, Lightbulb, Check, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services, brand, aiosFeatured } from '../mock';

const AgentsVisual = () => (
  <div className="flex flex-col gap-3">
    <div className="flex items-start gap-2">
      <span className="mt-1 w-2 h-2 rounded-full bg-[#C2603A]" />
      <div className="flex-1 rounded-xl bg-white border border-ink-10 px-3 py-2 text-[13px] text-ink/85">Found 12 new leads matching your ICP</div>
    </div>
    <div className="flex justify-end">
      <div className="rounded-xl bg-[#C2603A] text-white px-3 py-2 text-[13px] max-w-[75%]">Enrich and add to CRM sequence</div>
    </div>
    <div className="flex items-start gap-2">
      <span className="mt-1 w-2 h-2 rounded-full bg-[#C2603A]" />
      <div className="flex-1 rounded-xl bg-white border border-ink-10 px-3 py-2 text-[13px] text-ink/85">Done. 12 contacts enriched, emails drafted.</div>
    </div>
    <div className="flex gap-1 pl-1">
      <span className="w-1.5 h-1.5 rounded-full bg-ink/30 animate-pulse" />
      <span className="w-1.5 h-1.5 rounded-full bg-ink/30 animate-pulse [animation-delay:150ms]" />
      <span className="w-1.5 h-1.5 rounded-full bg-ink/30 animate-pulse [animation-delay:300ms]" />
    </div>
  </div>
);

const WorkflowsVisual = () => {
  const nodes = [
    { label: 'Webhook', icon: Workflow },
    { label: 'Transform', icon: Bot },
    { label: 'Slack', icon: Bot },
    { label: 'Sheets', icon: Database },
  ];
  return (
    <div className="flex items-center justify-between gap-2">
      {nodes.map((n, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center gap-2">
            <div className="w-11 h-11 rounded-xl bg-white border border-ink-10 flex items-center justify-center soft-card">
              <n.icon className="w-5 h-5 text-[#C2603A]" />
            </div>
            <span className="text-[11px] text-muted">{n.label}</span>
          </div>
          {i < nodes.length - 1 && (
            <div className="flex-1 h-[2px] bg-gradient-to-r from-[#C2603A]/40 to-[#C2603A]/10 relative">
              <span className="absolute -top-1 right-0 w-2 h-2 rotate-45 border-t-2 border-r-2 border-[#C2603A]/40" />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const RagVisual = () => (
  <div className="flex flex-col gap-3">
    <div className="rounded-xl bg-white border border-ink-10 px-3 py-2 text-[13px] text-ink/85">{"What\u2019s the refund policy for enterprise?"}</div>
    <div className="flex gap-2">
      {['terms.pdf', 'faq.md', 'policy.pdf'].map((f) => (
        <span key={f} className="text-[11px] px-2 py-1 rounded-md bg-[#FCE7D6] text-[#8a3a1c] border border-[#C2603A]/20">{f}</span>
      ))}
    </div>
    <div className="rounded-xl bg-[#FFF5EC] border border-[#C2603A]/20 px-3 py-2 text-[13px] text-ink/85">
      Enterprise refunds are processed within 30 days. Full details in <span className="text-[#C2603A]">policy.pdf</span> section 4.2{"\u2026"}
    </div>
  </div>
);

const StrategyVisual = () => {
  const phases = [
    { label: 'Phase 1 \u2014 Audit', w: '20%', sub: 'Week 1-2' },
    { label: 'Phase 2 \u2014 Quick Wins', w: '35%', sub: 'Week 3-4' },
    { label: 'Phase 3 \u2014 Core Build', w: '60%', sub: 'Week 5-10' },
    { label: 'Phase 4 \u2014 Scale', w: '85%', sub: 'Week 11-12' },
  ];
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[11px] text-muted uppercase tracking-wider">Implementation Roadmap</div>
      {phases.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className="flex justify-between text-[12px] mb-1"><span>{p.label}</span><span className="text-muted">{p.sub}</span></div>
            <div className="h-1.5 rounded-full bg-ink/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#E08C5F] to-[#C2603A] rounded-full" style={{ width: p.w }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const VisualMap = {
  agents: AgentsVisual,
  workflows: WorkflowsVisual,
  rag: RagVisual,
  strategy: StrategyVisual,
};

const Services = () => {
  return (
    <section id="services" className="relative py-24 md:py-32">
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div className="relative mx-auto max-w-6xl px-5">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-[#C2603A]">
            <span className="w-5 h-px bg-[#C2603A]/60" /> What We Build <span className="w-5 h-px bg-[#C2603A]/60" />
          </div>
          <h2 className="serif-display text-4xl md:text-6xl mt-4 text-ink">
            AI Solutions That Deliver Results
          </h2>
        </div>

        {/* Featured AIOS card */}
        <Link
          to={aiosFeatured.ctaHref}
          className="group relative block mt-14 rounded-2xl overflow-hidden border border-ink-10 soft-card bg-white hover:-translate-y-0.5 transition-transform duration-300"
        >
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#E08C5F] to-[#C2603A]" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 p-7 md:p-9 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#C2603A] font-semibold">
                <Sparkles className="w-3.5 h-3.5" /> {aiosFeatured.eyebrow}
              </div>
              <h3 className="serif-display text-3xl md:text-4xl mt-3 text-ink leading-[1.1]">
                {aiosFeatured.title}
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-muted max-w-2xl">
                {aiosFeatured.description}
              </p>
              <ul className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2">
                {aiosFeatured.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[13.5px] text-ink/85">
                    <Check className="w-3.5 h-3.5 mt-1 text-[#C2603A] shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:pl-6 lg:border-l lg:border-ink-10 flex lg:flex-col items-start lg:items-end gap-3">
              <span className="btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[14px] font-medium group-hover:translate-x-0.5 transition-transform whitespace-nowrap">
                {aiosFeatured.ctaLabel} <ArrowRight className="w-4 h-4" />
              </span>
              <span className="text-[12px] text-muted">14-day install · owned by your team</span>
            </div>
          </div>
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          {services.map((s) => {
            const Visual = VisualMap[s.visual];
            return (
              <article
                key={s.id}
                className="group rounded-2xl bg-[#FBF5EC] border border-ink-10 p-6 md:p-7 soft-card hover:-translate-y-0.5 transition-transform duration-300"
              >
                <div className="rounded-xl bg-white/60 border border-ink-10 p-4 min-h-[170px] flex items-center">
                  <div className="w-full">
                    <Visual />
                  </div>
                </div>
                <h3 className="serif-display text-2xl md:text-3xl mt-6 text-ink">{s.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted">{s.description}</p>
              </article>
            );
          })}
        </div>

        <p className="text-center mt-12 text-muted">
          We also offer team workshops and AI training sessions.{' '}
          <a href="#contact" className="text-[#C2603A] hover:underline">
            Book a call to learn more <ArrowRight className="inline w-3.5 h-3.5" />
          </a>
        </p>
      </div>
    </section>
  );
};

export default Services;
